import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useToast } from "@chakra-ui/react";
import { fetchCategories, fetchCategoryItems, deleteItemAPI, updateCategorySequence, updateItemSequence } from '../APIs/menuAPI';
import { 
  setCategories, 
  setCategoryItems, 
  setOpenedCategories, 
  setPage, 
  setQuery, 
  toggleCategory, 
  deleteItem, 
  reorderCategories,
  reorderItems,
  updateItem
} from '../redux/Menu';
import { TOAST_MESSAGES, INTERSECTION_OBSERVER } from '../components/Menu/Constant';

export const useOptimizedMenuData = ({
  refreshTrigger,
  categoriesToRefetch,
  onCategoriesRefetched,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  
  const {
    categories,
    categoryItemsMap,
    openedCategories,
    query,
    page,
    hasMore,
  } = useSelector((state) => state.menu);

  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingItems, setLoadingItems] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [localQuery, setLocalQuery] = useState("");
  
  // Request cancellation
  const abortControllerRef = useRef(null);
  const pendingRequestsRef = useRef(new Set());
  const itemReorderTimeoutRef = useRef(null);
  const pendingReorderRef = useRef(null);
  const isDraggingRef = useRef(false);
  const searchDebounceRef = useRef(null);

  const { ref: inViewRef, inView } = useInView({
    threshold: INTERSECTION_OBSERVER.THRESHOLD,
    rootMargin: INTERSECTION_OBSERVER.ROOT_MARGIN,
  });

  // Optimized query with better caching
  const { data, isLoading, error } = useQuery({
    queryKey: ["categories", page, query, refreshTrigger],
    queryFn: async ({ signal }) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      pendingRequestsRef.current.add(abortControllerRef.current);
      
      try {
        const result = await fetchCategories({ page, query });
        return result;
      } finally {
        pendingRequestsRef.current.delete(abortControllerRef.current);
      }
    },
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000, // 2 minutes (reduced from 5)
    cacheTime: 5 * 60 * 1000, // 5 minutes (reduced from 10)
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false, // Changed to false for better performance
    retry: 1, // Reduce retries
    retryDelay: 1000,
  });

  // Cleanup on unmount
  useEffect(() => {
    const pendingRequests = pendingRequestsRef.current;
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pendingRequests.forEach(controller => controller.abort());
      if (itemReorderTimeoutRef.current) {
        clearTimeout(itemReorderTimeoutRef.current);
      }
    };
  }, []);

  // Sync Redux query into local state when query changes (e.g. clearSearch); intentionally omit localQuery to avoid overwriting user input
  useEffect(() => {
    if (query !== localQuery) {
      setLocalQuery(query || "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when Redux query changes
  }, [query]);

  // Debounced search effect to prevent excessive API calls - only updates Redux after user stops typing
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      if (localQuery !== query) {
        dispatch(setQuery(localQuery));
        if (localQuery === "" || localQuery.length >= 2) {
          dispatch(setPage(1));
        }
      }
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [localQuery, query, dispatch]);

  // Optimized categories update
  useEffect(() => {
    if (data) {
      dispatch(setCategories({
        data: data.data,
        meta: data.meta,
        page,
        append: page > 1
      }));
    }
  }, [data, page, dispatch]);

  // Error handling
  useEffect(() => {
    if (error) {
      toast({
        ...TOAST_MESSAGES.ERROR.LOAD_CATEGORIES,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  // Optimized infinite scroll with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inView && hasMore && !isLoading && data?.meta?.totalPages && page < data.meta.totalPages) {
        dispatch(setPage(page + 1));
      }
    }, 100); // Small debounce to prevent rapid firing

    return () => clearTimeout(timeoutId);
  }, [inView, hasMore, isLoading, page, dispatch, data]);

  // Batch fetch items for multiple categories
  const batchFetchItems = useCallback(async (categoryIds) => {
    const uncachedCategories = categoryIds.filter(id => 
      !categoryItemsMap[id] || categoryItemsMap[id].length === 0
    );

    if (uncachedCategories.length === 0) return;

    // Set loading state for all categories at once
    setLoadingItems(prev => {
      const newState = { ...prev };
      uncachedCategories.forEach(id => newState[id] = true);
      return newState;
    });

    try {
      // Fetch items for all uncached categories in parallel
      const promises = uncachedCategories.map(async (categoryId) => {
        try {
          const itemsArray = await fetchCategoryItems(categoryId);
          return { categoryId, items: itemsArray };
        } catch (error) {
          console.error(`Error fetching items for category ${categoryId}:`, error);
          return { categoryId, items: [] };
        }
      });

      const results = await Promise.allSettled(promises);
      
      // Update state for all successful results
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const { categoryId, items } = result.value;
          dispatch(setCategoryItems({ categoryId, items }));
        }
      });
    } catch (error) {
      console.error("Error in batch fetch:", error);
    } finally {
      // Clear loading state for all categories
      setLoadingItems(prev => {
        const newState = { ...prev };
        uncachedCategories.forEach(id => newState[id] = false);
        return newState;
      });
    }
  }, [categoryItemsMap, dispatch]);

  // Optimized category toggle with batch fetching
  const handleToggleCategory = useCallback((categoryId) => {
    dispatch(toggleCategory(categoryId));

    // If category is opening and has no items, fetch them
    if (!categoryItemsMap[categoryId]?.length && !loadingItems[categoryId]) {
      batchFetchItems([categoryId]);
    }
  }, [categoryItemsMap, loadingItems, dispatch, batchFetchItems]);

  // Handle categories to refetch
  useEffect(() => {
    if (categoriesToRefetch && categoriesToRefetch.length > 0) {
      batchFetchItems(categoriesToRefetch);
      onCategoriesRefetched();
    }
  }, [categoriesToRefetch, onCategoriesRefetched, batchFetchItems]);

  // Optimized search with debouncing
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    
    // Update local state immediately for responsive UI (no API call)
    setLocalQuery(value);
  }, []);

  const clearSearch = useCallback(() => {
    setLocalQuery("");
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    dispatch(setQuery(""));
  }, [dispatch]);

  // Optimized reorder with optimistic updates
  const handleReorder = useCallback(async (newOrder) => {
    // Optimistic update
    dispatch(reorderCategories(newOrder));
    
    try {
      const reorderedData = newOrder.map((id, index) => ({
        id: parseInt(id),
        sequence_number: index + 1,
      }));
      
      await updateCategorySequence(reorderedData);
      
      toast({ 
        ...TOAST_MESSAGES.SUCCESS.CATEGORIES_REORDERED, 
        status: "success", 
        duration: 3000, 
        isClosable: true 
      });
    } catch (error) {
      // Revert on error
      toast({ 
        ...TOAST_MESSAGES.ERROR.UPDATE_ORDER, 
        status: "error", 
        duration: 5000, 
        isClosable: true 
      });
      // You might want to revert the optimistic update here
    }
  }, [dispatch, toast]);

  // Handle reordering items within a category - only updates UI, API call on drop
  const handleReorderItems = useCallback((categoryId, oldOrder, newOrder) => {
    // Find the dragged item (first item that changed position)
    let draggedItem = null;
    let draggedNewIndex = -1;
    
    for (let i = 0; i < oldOrder.length; i++) {
      const oldItem = oldOrder[i];
      const newIndex = newOrder.findIndex(item => item.id === oldItem.id);
      if (newIndex !== i) {
        draggedItem = oldItem;
        draggedNewIndex = newIndex;
        break;
      }
    }
    const displacedItem = oldOrder[draggedNewIndex];
    const displacedNewIndex = newOrder.findIndex(item => item.id === displacedItem?.id);    
    const reorderedItems = newOrder.map((item, index) => ({
      ...item,
      sequence_number: index,
    }));
    dispatch(reorderItems({ categoryId, newOrder: reorderedItems }));
    if (draggedItem) {
      const itemsToUpdate = [];
      
      if (draggedItem) {
        itemsToUpdate.push({
          id: draggedItem.id,
          sequence_number: draggedNewIndex,
        });
      }
      
      if (displacedItem && displacedItem.id !== draggedItem?.id && displacedNewIndex !== -1) {
        itemsToUpdate.push({
          id: displacedItem.id,
          sequence_number: displacedNewIndex,
        });
      }
      
      pendingReorderRef.current = {
        categoryId,
        itemsToUpdate,
      };
      isDraggingRef.current = true;
    }
  }, [dispatch]);

  const handleDragEnd = useCallback(async () => {
    if (!isDraggingRef.current || !pendingReorderRef.current) {
      return;
    }
    
    isDraggingRef.current = false;
    const { categoryId, itemsToUpdate } = pendingReorderRef.current;
    pendingReorderRef.current = null;
    
    if (itemReorderTimeoutRef.current) {
      clearTimeout(itemReorderTimeoutRef.current);
      itemReorderTimeoutRef.current = null;
    }
    
    try {
      if (itemsToUpdate.length > 0) {
        await updateItemSequence(categoryId, itemsToUpdate);
        
        toast({ 
          title: "Items reordered",
          description: "Items have been reordered successfully.",
          status: "success", 
          duration: 3000, 
          isClosable: true 
        });
      }
    } catch (error) {
      toast({ 
        title: "Error updating order",
        description: "Failed to update item order. Please try again.",
        status: "error", 
        duration: 5000, 
        isClosable: true 
      });
    }
  }, [toast]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) {
        handleDragEnd();
      }
    };
    
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('pointerup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('pointerup', handleGlobalMouseUp);
    };
  }, [handleDragEnd]);

  // Optimized delete with immediate UI update
  const handleDelete = useCallback(async (itemId = null) => {
    const idToDelete = itemId || selectedItem;
    if (!idToDelete) return;

    try {
      // Optimistic update - remove item immediately
      dispatch(deleteItem(idToDelete));
      
      // Update query cache without refetching
      queryClient.setQueryData(["categories", page, query], (oldData) => {
        if (!oldData) return oldData;
        
        return {
          ...oldData,
          data: oldData.data.map(category => ({
            ...category,
            items: category.items?.filter(itemId => itemId !== idToDelete.toString()) || [],
            itemsData: category.itemsData?.filter(item => item.id !== idToDelete) || []
          }))
        };
      });
      
      setSelectedItem(null);
      
      // Show success message
      toast({
        ...TOAST_MESSAGES.SUCCESS.ITEM_DELETED,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Delete from server in background
      await deleteItemAPI(idToDelete);
      
    } catch (error) {
      console.error("Error deleting item:", error);
      
      // Revert optimistic update on error
      toast({
        ...TOAST_MESSAGES.ERROR.DELETE_ITEM,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      
      // You might want to revert the optimistic update here
    }
  }, [selectedItem, toast, dispatch, queryClient, page, query]);

  // Optimized item update
  const handleItemUpdate = useCallback((updatedItem) => {
    // Update Redux state immediately
    dispatch(updateItem(updatedItem));
    
    // Update query cache without refetching
    queryClient.setQueryData(["categories", page, query], (oldData) => {
      if (!oldData) return oldData;
      
      return {
        ...oldData,
        data: oldData.data.map(category => ({
          ...category,
          itemsData: category.itemsData?.map(item => 
            item.id === updatedItem.id ? { ...item, ...updatedItem } : item
          ) || []
        }))
      };
    });
    
    // Update categoryItemsMap
    for (const categoryId in categoryItemsMap) {
      const items = categoryItemsMap[categoryId];
      const itemIndex = items.findIndex(item => item.id === updatedItem.id);
      if (itemIndex !== -1) {
        dispatch(setCategoryItems({ 
          categoryId, 
          items: items.map((item, index) => 
            index === itemIndex ? { ...item, ...updatedItem } : item
          )
        }));
        break;
      }
    }
  }, [dispatch, queryClient, page, query, categoryItemsMap]);

  // Toggle all categories
  const toggleAllCategories = useCallback(() => {
    if (expandAll) {
      dispatch(setOpenedCategories([]));
    } else {
      const categoryIds = categories.map(cat => cat.id);
      dispatch(setOpenedCategories(categoryIds));
      
      // Batch fetch items for all categories
      batchFetchItems(categoryIds);
    }
    setExpandAll(!expandAll);
  }, [categories, expandAll, dispatch, batchFetchItems]);

  return {
    categories,
    categoryItemsMap,
    openedCategories,
    query,
    localQuery,
    hasMore,
    loadingItems,
    expandAll,
    selectedItem,
    isLoading,
    error,
    inViewRef,
    handleSearchChange,
    clearSearch,
    handleToggleCategory,
    handleReorder,
    handleReorderItems,
    toggleAllCategories,
    confirmDelete: setSelectedItem,
    handleDelete,
    setSelectedItem,
    handleItemUpdate,
    // Add these missing variables
    data,
    page,
  };
};
